import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import { MeetingsService, MetricType } from '../meetings.service'
import { SpeakerXRayMeeting } from './speaker-x-ray-meeting'
import { TranslateService } from '@ngx-translate/core'
import { FormsModule } from '@angular/forms'
import { SpeakerXRayItemComponent } from './speaker-x-ray-item/speaker-x-ray-item.component'
import { ToastService } from '../toast.service'
import { ActivatedRoute, Router } from '@angular/router'
import { useMediaTimePrecision } from '../media-player-controller/media-player-controller.component'
import { interval, Subscription, takeWhile } from 'rxjs'
import { LoggerService } from '../logger.service'
import { generateXRaySpeakers, SpeakerSlot } from './x-ray-speaker-generation'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { MetricsService } from '../metrics.service'

export const MINIMUM_SEGMENT_DURATION = 10

@Component({
  selector: 'app-speaker-x-ray',
  standalone: true,
  imports: [FormsModule, SpeakerXRayItemComponent],
  templateUrl: './speaker-x-ray.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './speaker-x-ray.component.scss',
})
export class SpeakerXRayComponent implements OnChanges, OnDestroy {
  @Input({ required: true })
  meeting!: SpeakerXRayMeeting

  @Input({ required: true })
  canEdit!: boolean

  @Input({ required: true })
  canReprocessOutputs!: boolean

  @Input({ required: true })
  showXRay!: boolean

  @Input({ required: true })
  labelingModeEnabled!: boolean

  @Input()
  currentProgress: number = 0

  @Input()
  activeSpeakerId: string | null = null

  @Output()
  selectTime = new EventEmitter<number>()

  router = inject(Router)
  route = inject(ActivatedRoute)
  meetingsService = inject(MeetingsService)
  toastService = inject(ToastService)
  loggerService = inject(LoggerService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  splitSpeakersMutation = this.meetingsService.splitSpeakersMutation()
  patchSpeakerNameMutation = this.meetingsService.patchSpeakerNameMutation()
  reprocessOutputsMutation = this.meetingsService.reprocessOutputsMutation()
  metricsService = inject(MetricsService)
  speakers: SpeakerSlot[] = []
  segmentPlaybackSubscription: Subscription | null = null
  indentationInSeconds = 1
  current_unlabeled_speaker_index = 0

  get trackerLocation(): string {
    return this.currentProgress < 100 ? `${this.currentProgress}%` : 'calc(100% - 1px)'
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['meeting']) {
      if (
        this.meeting.duration === null ||
        this.meeting.duration === undefined ||
        this.meeting.userSpeakerMapping === null ||
        this.meeting.userSpeakerMapping === undefined
      ) {
        this.speakers = []
      } else {
        this.speakers = generateXRaySpeakers(
          this.meeting.speakers,
          this.meeting.duration,
          this.meeting.userSpeakerMapping,
          this.meeting.lastSpeechEndTime ?? null
        )
      }
      if (this.labelingModeEnabled) {
        this.playUnlabeledSpeakerSegments()
      }
    }
    if (changes['activeSpeakerId'] && this.activeSpeakerId !== null) {
      this.scrollToActiveSpeaker(this.activeSpeakerId)
    }
    if (changes['labelingModeEnabled']) {
      if (this.labelingModeEnabled) {
        this.playUnlabeledSpeakerSegments()
      } else {
        this.disableLabelingMode()
      }
    }
  }

  ngOnDestroy() {
    if (this.segmentPlaybackSubscription !== null) {
      this.segmentPlaybackSubscription.unsubscribe()
    }
  }

  displayReprocessDialog() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.SPEAKERS.REPROCESS_DIALOG_TITLE'),
        description: this.translateService.instant('MEETINGS.SPEAKERS.REPROCESS_DIALOG_DESCRIPTION'),
        confirmLabel: this.translateService.instant('COMMON.YES'),
        cancelLabel: this.translateService.instant('COMMON.NOT_NOW'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.reprocessOutputsMutation.mutateAsync({ meetingId: this.meeting.id })
      }
    })
  }

  private playUnlabeledSpeakerSegments() {
    const currentUnlabeledSpeaker = this.currentUnlabeledSpeaker
    if (currentUnlabeledSpeaker === null) {
      console.warn('No unlabeled speaker found. Exiting labeling mode.')
      this.metricsService.sendMetric(this.meeting.id, MetricType.DISABLE_SPEAKER_LABELING_MODE, { completed: true })
      this.disableLabelingMode()
      if (this.canReprocessOutputs) {
        this.displayReprocessDialog()
      }
      return
    }
    if (this.segmentPlaybackSubscription !== null) {
      this.segmentPlaybackSubscription.unsubscribe()
    }
    this.playSpeakerSegments(currentUnlabeledSpeaker)
  }

  private playSpeakerSegments(speaker: SpeakerSlot) {
    const segments = speaker.segments.filter((segment) => segment.duration > MINIMUM_SEGMENT_DURATION)
    const segmentStartEndTimes = segments.map((segment) => ({
      startTime: segment.startTimeSeconds + this.indentationInSeconds,
      endTime: segment.startTimeSeconds + segment.duration,
    }))
    if (segmentStartEndTimes.length === 0) {
      return
    }
    const processSegment = (index: number) => {
      if (this.currentUnlabeledSpeaker === null) {
        this.loggerService.info(`No unlabeled speaker found. Exiting labeling mode.`)
        this.disableLabelingMode()
        return
      }
      if (index >= segments.length) {
        if (this.segmentPlaybackSubscription !== null) {
          this.segmentPlaybackSubscription.unsubscribe()
        }
        this.loggerService.info(`All segments processed for the speaker, playing segments again`)
        processSegment(0)
        return
      }
      const { startTime, endTime } = segmentStartEndTimes[index]
      this.setMediaTime(startTime)
      this.loggerService.info(`Setting media time to ${startTime} to play the segment with end time ${endTime}`)
      this.segmentPlaybackSubscription = interval(250)
        .pipe(takeWhile(() => this.getMediaTime() < endTime - 1))
        .subscribe({
          complete: () => {
            this.loggerService.info(`Threshold reached for segment ${index + 1}`)
            processSegment(index + 1)
          },
        })
    }
    processSegment(0)
  }

  disableLabelingMode() {
    this.router.navigate([], {
      queryParams: { speakerLabelingModeEnabled: null, showSpeakes: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
    if (this.segmentPlaybackSubscription !== null) {
      this.segmentPlaybackSubscription.unsubscribe()
    }
  }

  skipLabelingSpeaker() {
    this.current_unlabeled_speaker_index = (this.current_unlabeled_speaker_index + 1) % this.unlabeledSpeakers.length
    this.playUnlabeledSpeakerSegments()
  }

  get currentUnlabeledSpeaker(): SpeakerSlot | null {
    return this.unlabeledSpeakers[this.current_unlabeled_speaker_index] ?? null
  }

  get unlabeledSpeakers(): SpeakerSlot[] {
    if (this.meeting.userSpeakerMapping === null) {
      return []
    }
    return this.speakers
      .filter((speaker) => speaker.segments.some((segment) => segment.duration > MINIMUM_SEGMENT_DURATION))
      .filter((speaker) => this.meeting.userSpeakerMapping![speaker.speaker.speakerTag] === speaker.speaker.speakerTag)
  }

  isLabelingModeFocusedOnSpeaker(speaker: SpeakerSlot): boolean {
    return this.labelingModeEnabled && this.currentUnlabeledSpeaker !== null && this.currentUnlabeledSpeaker.speaker.speakerTag === speaker.speaker.speakerTag
  }

  async splitSpeakers(speakerTag: string) {
    await this.splitSpeakersMutation.mutateAsync({ meetingId: this.meeting.id, speakerTag })
  }

  async updateSpeakerName(speakerTag: string, speakerName: string) {
    this.toastService.show('MEETINGS.SPEAKERS.SPEAKER_NAME_UPDATING')
    await this.patchSpeakerNameMutation.mutateAsync({ meetingId: this.meeting.id, speakerTag, speakerName })
    this.toastService.show('MEETINGS.SPEAKERS.SPEAKER_NAME_UPDATED')
    this.metricsService.sendMetric(this.meeting.id, MetricType.ASSIGN_SPEAKER_VIA_XRAY)
  }

  private scrollToActiveSpeaker(activeSpeakerId: string) {
    const speakerElement = document.getElementById(`speaker-x-ray-${activeSpeakerId}`) ?? null
    if (speakerElement !== null) {
      speakerElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  private async setMediaTime(startTimeSeconds: number) {
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        mediaTime: useMediaTimePrecision(startTimeSeconds),
        forceMediaTimeUpdate: true,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  private getMediaTime(): number {
    return parseFloat(this.route.snapshot.queryParams['mediaTime']) ?? 0
  }
}
